import * as d3 from 'd3'
import { motion } from 'motion/react'
import * as React from 'react'

import { cn } from '@/utils'
import { CHART_COLORS, NEGATIVE_BAR_COLOR } from './Constants'
import { ChartControls } from './Controls'
import { ChartGrid } from './Grid'
import { useChartBase } from './Hooks'
import { ChartTooltip } from './Tooltip'
import type { ChartDataPoint, ChartExportProps } from './types'
import {
  formatChartValue,
  granularityDateFormatter,
  inferDateGranularity,
  parseBucketDate,
} from './utils/formatters'

interface BarChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChartExportProps {
  data?: ChartDataPoint[]
  className?: string
  height?: number
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  color?: string
  radius?: number
  onBarClick?: (data: ChartDataPoint) => void
  rotateXLabels?: boolean
  truncateXLabels?: boolean
  maxLabelLength?: number
  tickSpacing?: number
  onChartReady?: (params: {
    containerRef: React.RefObject<HTMLDivElement | null>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  xTickFormatter?: (value: any) => string
  yTickFormatter?: (value: any) => string
  valueFormat?: 'currency' | 'number'
  showHoverControls?: boolean
}

const BarChart = React.forwardRef<HTMLDivElement, BarChartProps>(
  (
    {
      data = [],
      className,
      height = 400,
      marginTop = 20,
      marginRight = 30,
      marginBottom = 30,
      marginLeft = 40,
      color = CHART_COLORS[0],
      radius = 4,
      onBarClick,
      rotateXLabels = false,
      truncateXLabels = false,
      maxLabelLength = 12,
      tickSpacing,
      onChartReady,
      xTickFormatter,
      yTickFormatter,
      valueFormat,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      showHoverControls = false,
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      dimensions,
      isFullscreen,
      toggleFullscreen,
      fullscreenError,
      fullscreenSupported,
      hoveredData,
      setHoveredData,
    } = useChartBase(onChartReady)

    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement)

    // When the caller declares a value unit (chat charts always do), format
    // the y-axis ticks and tooltip with it; otherwise keep the raw numbers.
    const formatValue = (value: number): string =>
      valueFormat ? formatChartValue(value, valueFormat) : String(value)
    const resolvedYTickFormatter =
      yTickFormatter ??
      (valueFormat ? (value: any) => formatValue(Number(value)) : undefined)

    const { processedData, isCategorical, dateGranularity } =
      React.useMemo(() => {
        const processed = data.map((d) => ({
          ...d,
          dateObj: parseBucketDate(d.date) ?? undefined,
        }))
        const categorical = processed.length > 0 && !processed[0].dateObj
        const validDates = processed
          .map((point) => point.dateObj)
          .filter((dateObj): dateObj is Date => dateObj !== undefined)
        return {
          processedData: processed,
          isCategorical: categorical,
          dateGranularity: inferDateGranularity(validDates),
        }
      }, [data])

    // Labels the x-axis and tooltip at the bucket granularity (year/month/day)
    // so a yearly chart reads "2020", not "Dec 2020". The agent can override a
    // point's label with its own `formattedDate`.
    const formatBucketDate = granularityDateFormatter(dateGranularity)
    const labelForPoint = (point: (typeof processedData)[number]): string =>
      point.formattedDate ??
      (point.dateObj ? formatBucketDate(point.dateObj) : point.date)

    const { xScale, yScale } = React.useMemo(() => {
      if (!dimensions.width || !dimensions.height || !processedData.length)
        return {}

      const x = d3
        .scaleBand()
        .domain(processedData.map((d) => d.date))
        .range([marginLeft, dimensions.width - marginRight])
        .padding(0.3)

      // Include both the min and max so negative values (e.g. monthly
      // net cash flow with shortfall months) get their own range
      // below zero. Always anchor zero inside the domain so the bar
      // baseline at yScale(0) is on-chart, and a chart with only
      // positives (or only negatives) still renders with zero at one
      // edge.
      const numericValues = processedData
        .map((d) => Number(d.value))
        .filter((value) => !Number.isNaN(value))
      const dataMin = numericValues.length ? Math.min(...numericValues) : 0
      const dataMax = numericValues.length ? Math.max(...numericValues) : 0
      const domainMin = Math.min(0, dataMin)
      const domainMax = Math.max(0, dataMax)

      const y = d3
        .scaleLinear()
        .domain([domainMin, domainMax])
        .nice()
        .range([dimensions.height - marginBottom, marginTop])

      return { xScale: x, yScale: y }
    }, [
      processedData,
      dimensions,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    ])

    const handlePointerMove = (d: ChartDataPoint) => {
      setHoveredData(d)
    }

    const handlePointerLeave = () => {
      setHoveredData(null)
    }

    if (!processedData.length) return null

    return (
      <div
        ref={containerRef}
        className={cn(
          'w-full h-full relative group',
          isFullscreen && 'bg-white',
          className,
        )}
        {...props}
      >
        {dimensions.width > 0 && dimensions.height > 0 && xScale && yScale && (
          <svg
            role="img"
            aria-label="Bar chart"
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            className="overflow-visible"
          >
            <ChartGrid
              dimensions={dimensions}
              xScale={xScale}
              yScale={yScale}
              margin={{
                top: marginTop,
                right: marginRight,
                bottom: marginBottom,
                left: marginLeft,
              }}
              xTickFormatter={
                xTickFormatter || (isCategorical ? undefined : formatBucketDate)
              }
              yTickFormatter={resolvedYTickFormatter}
              fontSize={isFullscreen ? 14 : 10}
              rotateXLabels={rotateXLabels}
              truncateXLabels={truncateXLabels}
              maxLabelLength={maxLabelLength}
              tickSpacing={tickSpacing}
            />

            <g>
              {processedData.map((d, i) => {
                const value = Number(d.value)
                const safeValue = Number.isNaN(value) ? 0 : value
                const zeroY = yScale(0)
                const barHeight = Math.abs(zeroY - yScale(safeValue))
                const barY = Math.min(yScale(safeValue), zeroY)
                const fill =
                  d.color || (safeValue < 0 ? NEGATIVE_BAR_COLOR : color)

                return (
                  <motion.rect
                    key={i}
                    x={xScale(d.date)}
                    width={xScale.bandwidth()}
                    rx={radius}
                    ry={radius}
                    fill={fill}
                    initial={{ y: zeroY, height: 0 }}
                    animate={{ y: barY, height: barHeight }}
                    transition={{
                      duration: 0.6,
                      ease: 'easeOut',
                      delay: i * 0.025,
                    }}
                    onPointerEnter={() => handlePointerMove(d)}
                    onPointerLeave={handlePointerLeave}
                    onClick={() => onBarClick?.(d)}
                    className="opacity-90 hover:opacity-100 transition-opacity cursor-pointer"
                  />
                )
              })}
            </g>
          </svg>
        )}

        {showHoverControls && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={fullscreenError}
            fullscreenSupported={fullscreenSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={
            hoveredData && xScale && yScale
              ? {
                  x: xScale(hoveredData.date)! + xScale.bandwidth() / 2,
                  y: yScale(
                    Number.isNaN(Number(hoveredData.value))
                      ? 0
                      : Number(hoveredData.value),
                  ),
                }
              : undefined
          }
        >
          {hoveredData && (
            <>
              <div className="font-semibold">
                {Number.isNaN(Number(hoveredData.value))
                  ? formatValue(0)
                  : formatValue(Number(hoveredData.value))}
              </div>
              <div className="text-muted-foreground opacity-80">
                {labelForPoint(hoveredData)}
              </div>
            </>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
BarChart.displayName = 'BarChart'

export { BarChart }
